import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, throwError } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { reportCriticalError } from '../../utils/sentry-reporter';
import { ApiUtils } from '../helper/api.utils';

export const errorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Subscription lock. Must be handled BEFORE the 403 branch below, which logs the user out — a blocked
      // user has to stay signed in to reach the reactivation page and pay.
      // Matched on the error code rather than the status: every current path (the action filter and the
      // service-level SubscriptionBlockedException) answers 402, but keying on the code means a future check
      // that surfaces the same code with a different status still routes here instead of falling through to
      // the logout branch.
      if (ApiUtils.createAppErrorFromResponse(error).errorCode === 'SubscriptionExpired') {
        router.navigate(['/subscription-expired']);
        return throwError(() => error);
      }

      // Handle 403 Forbidden errors (authentication handled by token interceptor)
      if (error.status === 403) {
        console.log(`Forbidden error (403) for ${req.url}, redirecting to login`);
        authService.handleAuthenticationFailure();
        router.navigate(['/auth/login']);
        return throwError(() => error);
      }

      // Handle other client errors (4xx)
      if (error.status >= 400 && error.status < 500) {
        console.warn(`Client error (${error.status}) for ${req.url}:`, error.message);
      }

      // Report gateway/timeout errors to Sentry (502, 503, 504) – often not logged by the app server
      if (error.status === 502 || error.status === 503 || error.status === 504) {
        reportCriticalError('http_gateway_timeout', error, { url: req.url, status: error.status });
      }

      // For all other errors, just pass them through
      return throwError(() => error);
    })
  );
};
